/* eslint no-use-before-define: 0 */
import { nextTick } from 'vue';
import { defineStore, acceptHMRUpdate } from 'pinia';
import JsonPatchSrv from '@/js/services/JsonPatchSrv';
import useSelectionStore from '@/stores/selection';
import useUndoRedoStore from '@/stores/undoredo';
import usePlanningStore from './planning';
import { commitAdd, commitRemove, commitEmpty, insertNewElement, addElementHelper } from './elements/addremove';
import { updateDependencies } from './elements/dependencies';
import { setSnapBoundaries, snapToBoundaries } from './elements/snap';

let undoStateData = {}; // { `id=${el.id}`: el.data }

/** ********** */
/* UNDO/REDO */
/** ********** */
function undoredoElements() {
  const { planning } = usePlanningStore();
  if (Object.keys(undoStateData).length == planning.elements.length && planning.elements.every(el => el.getData() === undoStateData[`id=${el.id}`])) return;
  const newState = planning.elements.reduce((acc, el) => {
    acc[`id=${el.id}`] = el.getData(); // not using getAll to keep null values for jsonpatch
    return acc;
  }, {});

  const newStatePatches = JsonPatchSrv.getElementsDiff(undoStateData, newState);
  const oldStatePatches = JsonPatchSrv.getElementsDiff(newState, undoStateData);
  useUndoRedoStore().add({
    action: () => {
      const modifications = planning.applyContentPatch(newStatePatches, false);
      planning.elements.forEach((el) => {
        if (modifications.elements[el.id]) {
          el.update();
          setTimeout(() => el.updateHeight());
        }
      });
    },
    rollback: () => {
      const modifications = planning.applyContentPatch(oldStatePatches, false);
      planning.elements.forEach((el) => {
        if (modifications.elements[el.id]) {
          el.update();
          setTimeout(() => el.updateHeight());
        }
      });
    },
  });
  usePlanningStore().save();
}

function undoredoElement(el) {
  const undoStateElData = undoStateData[`id=${el.id}`];
  if (! undoStateElData) { undoredoElements(); return; }
  const newState = el.getData();
  if (newState === undoStateElData) return;
  useUndoRedoStore().add({
    action: () => {
      el.reset(newState);
      el.update();
    },
    rollback: () => {
      el.reset(undoStateElData);
      el.update();
    },
  });
  usePlanningStore().save();
}

/** ************ */
/** WORK DAYS * */
/** ************ */
function correctWorkdays() {
  const { planning } = usePlanningStore();
  if (! planning.timeline.workdays) return;
  planning.elements.forEach((el) => {
    const starttime = el.getStartTime();
    if (starttime.day() % 6 === 0) {
      el.setStartTime(starttime.addWithWorkdays(1, 'minute', planning.timeline.workdays).startOf('day'));
    }
    const endtime = el.getEndTime();
    if (endtime.day() % 6 === 0) {
      el.setEndTime(endtime.addWithWorkdays(-1, 'minute', planning.timeline.workdays).endOf('day'));
    }
  });
}

/** ******** */
/* DISPLAY */
/** ******** */
function updateAllElements() {
  const { planning } = usePlanningStore();
  planning.elements.forEach((el) => {
    el.update();
  });
}

let idCounter = 1;

const store = defineStore('planning-elements', {
  getters: {
    nextId() {
      const { planning } = usePlanningStore();
      idCounter = Math.max(idCounter, Math.max(...planning.elements.map(item => item.id), 0)) + 1;
      return idCounter;
    },
    getElement() {
      const { planning } = usePlanningStore();
      return id => planning.elements.find(el => el.id == id);
    },
  },

  actions: {
    add: commitAdd,
    remove: commitRemove,
    empty: commitEmpty,

    startChangingElement() {
      const { planning } = usePlanningStore();
      undoStateData = planning.elements.reduce((acc, el) => {
        acc[`id=${el.id}`] = el.getData();
        return acc;
      }, {});
    },

    changingElement(el) {
      const hasChanged = updateDependencies(el);
      if (hasChanged) return;
      if (el) {
        el.update();
        undoredoElement(el);
      } else {
        updateAllElements();
        undoredoElements();
      }
    },

    setSnapBoundaries,
    snapToBoundaries,

    addElements({ argstab, silent, extraProps }) {
      const planningStore = usePlanningStore();
      if (! silent) this.startChangingElement();

      const newels = [];
      (argstab || []).forEach((args) => {
        const newel = addElementHelper(args);
        if (extraProps) _.extend(newel, extraProps);
        newels.push(newel);
      });
      if (silent) return newels;

      const addedEls = newels.map(el => angular.copy(el));
      useUndoRedoStore().add({
        action: () => {
          addedEls.forEach((el) => { // todo : replace by getElementsDiff for consistency (no ref needs to be preserved here)
            insertNewElement(angular.copy(el.getAll()));
          });
        },
        rollback: () => {
          addedEls.forEach((el) => { // todo : replace by getElementsDiff
            commitRemove(this.getElement(el.id));
          });
        },
      });
      planningStore.save();
      return newels;
    },

    deleteElements({ els, silent }) {
      const planningStore = usePlanningStore();
      if (! silent) this.startChangingElement();

      (els || []).forEach((el) => {
        commitRemove(el);
        useSelectionStore().selection.delete(el.id);
      });
      if (silent) return;

      const deletedEls = angular.copy(els) || [];
      useUndoRedoStore().add({
        action: () => {
          deletedEls.forEach((el) => {
            commitRemove(this.getElement(el.id));
          });
        },
        rollback: () => {
          deletedEls.forEach((el) => {
            insertNewElement(angular.copy(el.getAll()));
          });
        },
      });
      planningStore.save();
    },

    onChangeTimeline(oldTimeline) {
      const { planning } = usePlanningStore();

      if (planning.timeline.workdays && oldTimeline && ! oldTimeline.workdays) {
        this.startChangingElement();
        correctWorkdays();
        undoredoElements();
      }

      nextTick(() => {
        updateAllElements();
        nextTick(() => {
          planning.elements.forEach((el) => {
            el.updateHeight();
          });
        });
      });
    },
  },
});

if (import.meta.hot) import.meta.hot.accept(acceptHMRUpdate(store, import.meta.hot));
export default store;
